
import { useNavigate } from "react-router-dom";
import BackgroundVisuals from "./BackgroundVisuals";

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "ai-consultation-advisory",
      title: "AI Consultation & Advisory",
      index: "01",
      desc: "Strategic AI guidance that transforms legacy business challenges into modern competitive advantages through proprietary frameworks.",
      image: "/images/ai-consultancy.jpeg",
      features: ["Readiness Assessment", "ROI Modeling", "Governance"]
    },
    {
      id: "ai-automation-digital-transformation",
      title: "AI Automation & Transformation",
      index: "02",
      desc: "Industrial-grade AI/MLOps implementations that identify and execute automation opportunities across your entire digital ecosystem.",
      image: "/images/ai_automation_and_digital.jpeg",
      features: ["Intelligent Agents", "Workflow Ops", "Neural Automations"]
    },
    {
      id: "data-intelligence-analytics-platform",
      title: "Data Intelligence & Analytics",
      index: "03",
      desc: "Transforming fragmented data into actionable intelligence through high-precision predictive modeling and real-time processing.",
      image: "/images/data_strategy.jpg",
      features: ["Predictive Models", "Real-time BI", "Data Pipelines"]
    }
  ];

  return (
    <div className="pt-24 min-h-screen bg-white relative selection:bg-[#952877]/10 selection:text-[#952877]">
      <BackgroundVisuals />
      
      {/* --- HEADER --- */}
      <section className="py-24 text-center relative z-10 px-6">
        <div className="max-w-4xl mx-auto">
          
          <h1 className="text-6xl md:text-8xl font-black text-[#17091a] mb-8 tracking-tighter leading-[1.1]">
            Architecting <br/> <span className="bg-gradient-to-r from-[#952877] via-[#592aa7] to-[#17091a] bg-clip-text text-transparent px-1 ">Intelligence</span>
          </h1>
        </div>
      </section>

      {/* --- VERTICAL STACKED CARDS --- */}
      <section className="pb-20 relative z-10 px-6">
        <div className="max-w-6xl mx-auto space-y-12">
          {services.map((service, idx) => (
            <div
              key={service.id}
              onClick={() => navigate(`/service/${service.id}`)}
              className={`group flex flex-col ${idx % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-10 md:gap-20 p-8 md:p-12 bg-[#fafafa] border border-gray-100 rounded-[3rem] hover:bg-white hover:shadow-[0_40px_100px_-20px_rgba(23,9,26,0.08)] hover:-translate-y-2 transition-all duration-700 cursor-pointer`}
            >
              {/* Image Container */}
              <div className="w-full md:w-1/2 aspect-[4/3] md:aspect-square overflow-hidden rounded-[2.5rem] relative shadow-2xl">
                <img 
                  src={service.image} 
                  alt={service.title} 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 grayscale-[0.2] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#17091a]/40 to-transparent"></div>
              
              </div>

              {/* Content Container */}
              <div className="w-full md:w-1/2 space-y-8">
                <div>
                  <h3 className="text-4xl md:text-5xl font-black text-[#17091a] mb-6 tracking-tighter group-hover:text-[#952877] transition-colors duration-500">
                    {service.title}
                  </h3>
                  <p className="text-gray-500 text-lg font-medium leading-relaxed">
                    {service.desc}
                  </p>
                </div>

                {/* Features Tag Cloud */}
                <div className="flex flex-wrap gap-3">
                  {service.features.map((f, i) => (
                    <span key={i} className="px-4 py-2 rounded-xl bg-white border border-gray-100 text-[10px] font-black uppercase tracking-widest text-[#17091a]">
                      {f}
                    </span>
                  ))}
                </div>

                {/* Vertical CTA Link */}
                <div className="flex items-center gap-4 group/link">
                  <div className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center group-hover:bg-[#17091a] group-hover:text-white transition-all duration-500">
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                  <span className="text-xs font-black uppercase tracking-[0.3em]">Explore more</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* --- INDUSTRIAL CTA SECTION --- */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto rounded-[4rem] bg-[#17091a] p-12 md:p-24 text-center relative overflow-hidden shadow-2xl">
          {/* Decorative Glow */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#952877]/20 blur-[100px] rounded-full"></div>
          
          <div className="relative z-10">
            <h2 className="text-4xl md:text-7xl font-black text-white mb-10 tracking-tighter">
              Ready to <span className="text-[#952877] italic">Initialize?</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-12 font-medium">
              Start your transformation journey today with our enterprise AI solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button 
                onClick={() => navigate('/contact')}
                className="w-full sm:w-auto px-12 py-6 bg-[#952877] text-white font-black text-xs uppercase tracking-[0.4em] rounded-2xl hover:bg-white hover:text-[#17091a] transition-all duration-500 shadow-xl"
              >
                Get Started
              </button>
              <button 
                onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
                className="w-full sm:w-auto px-12 py-6 border border-white/10 text-white font-black text-xs uppercase tracking-[0.4em] rounded-2xl hover:bg-white/5 transition-all"
              >
                Back to Top
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;


